import DataStructure, { sanitize } from "./DataStructure.js";
import {
  deepFreeze,
  formatCurrency,
  formatPercentage,
  roundPrice,
} from "../util/misc.js";
import { cloneDeep, isEmpty, isNumber } from "lodash";

export const INVOICE_TOTAL_FIELDS = {
  categories: {},
  items: 0,
  subtotal: 0,
  tax: 0,
  // Portion of this invoice that is the pass-through Shipping/Delivery
  // Charge. Counted in `subtotal`/`price` — the customer pays it and it is
  // taxable — but reported separately so `touchInvoice` can keep it out of
  // the COMMISSIONABLE actual pre-tax figure, matching the equivalent
  // exclusion from `expectedPreTax`.
  shipping: 0,
  discount: 0,
  addDiscount: 0,
  downPayment: 0,
  downPaymentFee: 0,
  downPaymentPercent: 0,
  payments: 0,
  // Sum of credit memo amounts applied to this invoice (see
  // `appliedTo` / `allocation` on `CreditMemo`). Starts at 0 for new
  // invoices and is maintained incrementally by
  // `functions/invoices.js::touchInvoice` when credit memos are
  // created / voided / deleted / edited. Subtracted from `due` /
  // `balance` so an invoice that is fully credited reads as $0 due.
  credit: 0,
  fee: 0,
  // Accrued late-payment finance charge (see `Invoice.financeCharge`). Like
  // `fee` (the credit-card surcharge) it is a pass-through charge to the
  // customer that ADDS to `balance` but is deliberately kept out of
  // `subtotal`/`price`, so it never enters the sale's commissionable
  // `actualPreTax` — matching how the delivery/credit-card fees are handled.
  // Non-taxable. Maintained incrementally by the daily accrual job
  // (`functions/invoices.js::accrueFinanceCharges`) and zeroed when the charge
  // is removed.
  financeCharge: 0,
  price: 0,
  balance: 0,
  due: 0,
};
deepFreeze(INVOICE_TOTAL_FIELDS);

export default class InvoiceTotal extends DataStructure {
  constructor(data = {}) {
    if (data && data.balance && !data.due) {
      data.due = data.balance;
    }

    super(sanitize(data, INVOICE_TOTAL_FIELDS));
  }

  // Formatting

  get tax() {
    return formatCurrency(this.data.tax);
  }

  get price() {
    return formatCurrency(this.data.price);
  }

  get due() {
    return formatCurrency(this.data.due);
  }

  get balance() {
    return formatCurrency(this.data.balance);
  }

  get subtotal() {
    return formatCurrency(this.data.subtotal);
  }

  get income() {
    return this.data.subtotal - this.data.discount;
  }

  get incomeFormatted() {
    return formatCurrency(this.income);
  }

  get discount() {
    return formatCurrency(this.data.discount);
  }

  get addDiscount() {
    return formatCurrency(this.data.addDiscount);
  }

  get fee() {
    return formatCurrency(this.data.fee);
  }

  get financeCharge() {
    return formatCurrency(this.data.financeCharge);
  }

  get downPayment() {
    return formatCurrency(this.data.downPayment);
  }

  get downPaymentFee() {
    return formatCurrency(this.data.downPaymentFee);
  }

  get downPaymentPercent() {
    return formatPercentage(this.data.downPaymentPercent);
  }

  get payments() {
    return formatCurrency(this.data.payments);
  }

  get credit() {
    return formatCurrency(this.data.credit);
  }

  get discountPercent() {
    return formatPercentage(
      this.data.discount && this.data.subtotal
        ? 100 * (this.data.discount / this.data.subtotal)
        : 0
    );
  }

  get addDiscountPercent() {
    const base = this.data.subtotal + this.data.tax - this.data.discount;
    return formatPercentage(
      this.data.addDiscount && base ? 100 * (this.data.addDiscount / base) : 0
    );
  }

  get categories() {
    const categories = {};
    Object.keys(this.data.categories).forEach((category) => {
      categories[category] = formatCurrency(this.data.categories[category]);
    });
    return isEmpty(categories) ? null : categories;
  }
}

// Compute totals
export function computeTotal({
  quoteSubtotal,
  quoteDiscount = 0,
  quotePrice = 0,
  downPaymentAmount = 0,
  downPaymentFee = 0,
  priorDownPaymentAllocated = 0,
  invoicePaymentAmount = 0,
  invoicePaymentFee = 0,
  invoiceAddDiscount = 0,
  invoiceCredit = 0,
  invoiceFinanceCharge = 0,
  priorTaxAllocated = 0,
  quoteTax,
  items,
  tax,
}) {
  const total = cloneDeep(INVOICE_TOTAL_FIELDS);

  if (tax && tax > 1) {
    tax /= 100;
  } else {
    tax = 0;
  }

  if (isNumber(downPaymentFee)) {
    total.downPaymentFee = downPaymentFee;
  }

  if (isNumber(invoicePaymentFee)) {
    total.fee = invoicePaymentFee;
  }

  if (isNumber(invoiceAddDiscount)) {
    total.addDiscount = invoiceAddDiscount;
  }

  if (isNumber(invoiceFinanceCharge)) {
    total.financeCharge = invoiceFinanceCharge;
  }

  // Iterate through items
  let quantity, itemPrice, category;
  let taxableSubtotal = 0;
  items.forEach((item) => {
    // A CHILD accessory (an item carrying a `parentID`) is a priced add-on
    // that rides with its parent unit — its value is bundled into the
    // parent line and it must never contribute to an invoice's count,
    // subtotal, price, or tax. Skipping it here mirrors the `!item.parentID`
    // exclusion in `computeQuoteTotals` (functions/sales-helpers.js) and the
    // `classifyItemBucket` accessory bucket in src/store/main/actions/sales.js,
    // and guards against a recompute/re-lock re-inflating the total if a
    // duplicate child ever retains a stale price. A STANDALONE accessory
    // (series "Accessories" but NO `parentID`) is the real deliverable and
    // still counts, since it has no `parentID`.
    if (item.parentID) return;

    // Material, Labor, or Misc
    quantity =
      !item.itemType || item.itemType === "material" ? item.quantity : 1;
    itemPrice = (item.price || 0) * quantity;
    taxableSubtotal +=
      tax && !(item.notax ?? item.itemType === "labor") ? itemPrice : 0;
    category = item.category || "Other";
    if (!(category in total.categories)) {
      total.categories[category] = 0;
    }
    total.categories[category] += itemPrice;
    if (item.itemType === "material") {
      total.items += quantity;
    }
    total.subtotal += itemPrice;
    total.price += itemPrice;
    if (item.isShipping) {
      total.shipping += itemPrice;
    }
  });

  // Clean up
  if (
    "Other" in total.categories &&
    Object.keys(total.categories).length === 1
  ) {
    total.categories = {};
  } else {
    Object.keys(total.categories).forEach((category) => {
      total.categories[category] = roundPrice(total.categories[category]);
    });
  }

  if (quoteDiscount && quoteSubtotal) {
    total.discount = roundPrice(
      quoteDiscount * (total.subtotal / quoteSubtotal)
    );
  }

  total.tax = roundPrice(tax * Math.max(0, taxableSubtotal - total.discount));

  // Reconcile per-invoice tax rounding against the sale-level tax so that the
  // sum of every invoice's tax equals the sale's single rounded tax figure.
  // Tax is rounded independently on each invoice, so `roundPrice(a) +
  // roundPrice(b)` can drift a cent from `roundPrice(a + b)` when a sale is
  // split across multiple invoices — stranding a phantom penny of balance on
  // the last invoice even though the sale is paid in full (e.g. sale
  // 50000005A: invoices summed to $10,187.86 vs the sale's $10,187.85). When
  // this invoice brings the cumulative tax to within a cent of the sale's tax
  // (i.e. it is effectively the final tax-bearing invoice), snap its tax to
  // absorb the remainder. A genuinely partial invoice — more taxable items
  // still to be billed — is off by far more than a cent, so the snap never
  // fires for it. Mirrors the `priorDownPaymentAllocated` reconciliation below.
  if (
    isNumber(quoteTax) &&
    total.tax > 0 &&
    roundPrice(priorTaxAllocated + total.tax) !== roundPrice(quoteTax) &&
    Math.abs(priorTaxAllocated + total.tax - quoteTax) < 0.02
  ) {
    total.tax = roundPrice(quoteTax - priorTaxAllocated);
  }

  total.price = roundPrice(total.price - total.discount + total.tax);

  if (downPaymentAmount && quoteSubtotal) {
    total.downPaymentPercent =
      Math.round((1e4 * total.subtotal) / quoteSubtotal) / 100;
    const totalDownPayment = roundPrice(downPaymentAmount + downPaymentFee);
    if (quotePrice && totalDownPayment >= quotePrice) {
      // Down payment covers the full sale price — set each invoice's
      // down payment to its price so that due is exactly $0.00, but never
      // claim more down payment than is actually left unallocated.
      //
      // Without the cap an invoice that bills MORE than the sale price claims
      // down payment that was never received and reads $0.00 due, right up
      // until `functions/downPaymentAllocation.js` apportions the real pool on
      // save and the balance reappears. That is what made prod invoice
      // 20260801 on sale 04500014A look settled while carrying $6,475.29: an
      // item quantity was corrected from 2 to 1 after the invoice had billed
      // 2, so the invoice exceeded the sale by exactly that much, and voiding
      // and re-creating the invoice reproduced the same $0.00.
      //
      // Splitting a sale across invoices can still leave the last one a cent
      // over from tax/discount rounding, and snapping that to $0.00 is the
      // whole point of this branch — so keep the 0.02 tolerance used by the
      // tax snap above and the prior-allocation reconciliation below, and only
      // cap an overage bigger than rounding can explain.
      // An invoice stamps the down-payment fee no other invoice carries yet
      // (see `getInvoiceAllocationPriors`) and `due` adds that same fee
      // straight back, so the fee is not consumed from the pool the way cash
      // is — only the unallocated cash limits the claim.
      const claim = roundPrice(total.price + total.downPaymentFee);
      const available = roundPrice(
        totalDownPayment - priorDownPaymentAllocated + total.downPaymentFee
      );
      total.downPayment =
        claim - available > 0.02 ? Math.max(0, available) : claim;
    } else {
      // Partial down payment — proportion by price/quotePrice
      const ratio = quotePrice
        ? total.price / quotePrice
        : total.subtotal / quoteSubtotal;
      const proportional = roundPrice(totalDownPayment * ratio);
      if (
        priorDownPaymentAllocated &&
        roundPrice(priorDownPaymentAllocated + proportional) !==
          totalDownPayment &&
        Math.abs(priorDownPaymentAllocated + proportional - totalDownPayment) <
          0.02
      ) {
        total.downPayment = roundPrice(
          totalDownPayment - priorDownPaymentAllocated
        );
      } else {
        total.downPayment = proportional;
      }
    }
  }
  if (invoicePaymentAmount) {
    total.payments = roundPrice(invoicePaymentAmount);
  }
  if (isNumber(invoiceCredit)) {
    total.credit = roundPrice(invoiceCredit);
  }
  total.subtotal = roundPrice(total.subtotal);
  total.due = roundPrice(
    total.price - total.downPayment + total.downPaymentFee - total.credit
  );
  total.balance = roundPrice(
    total.due -
      total.payments +
      total.fee -
      total.addDiscount +
      total.financeCharge
  );
  return total;
}
